import "../style/ApiTestingGuide.css";
import { NavLink } from "react-router-dom";
import apiTesting from "../assets/API Testing Cheat.png"

export default function ApiTestingGuide() {
  return (
    <div className="api-container">

      {/* ---------- MAIN CONTENT ---------- */}
      <main className="main-content-area">
        <h1 className="entry-title">
          API Testing – Detailed Questions & Answers (With Real-Time Scenarios)
        </h1>

        <div className="post-content">
          {/* API BASICS */}
          <section id="api-basics">
          <img src={apiTesting} alt ="api testing"></img>

            <h3>What is an API?</h3>
            <p>
              API (Application Programming Interface) allows two applications to
              communicate with each other using requests and responses without
              exposing internal logic.
            </p>

            <h3>What is API Testing?</h3>
            <p>
              API Testing validates backend services by verifying business
              logic, data accuracy, security, performance, and error handling
              without UI.
            </p>

            <div className="toc-container">
              <strong>Why API Testing is Important</strong>
              <ul>
                <li>Faster than UI testing</li>
                <li>Early bug detection</li>
                <li>Independent of frontend</li>
                <li>Ensures backend reliability</li>
                <li>Best suited for automation</li>
              </ul>
            </div>
          </section>

          {/* ARCHITECTURE */}
          <section id="architecture">
            <h2> API Architecture Flow</h2>
            <ul>
              <li>Client sends request</li>
              <li>API validates request</li>
              <li>Business logic executes</li>
              <li>Database interaction</li>
              <li>Response returned (JSON / XML)</li>
            </ul>

            <p>
              <strong>Real-Time Example:</strong>
            </p>
            <p>
              UI → Order API → Inventory Service → Payment Service → Database →
              Response
            </p>
          </section>

          {/* HTTP HTTPS */}
          <section id="http-https">
            <h2> HTTP & HTTPS</h2>

            <h3>Why HTTPS is Mandatory?</h3>
            <ul>
              <li>Encrypts sensitive data (token, password)</li>
              <li>Prevents man-in-the-middle attacks</li>
              <li>Ensures secure authentication</li>
            </ul>

            <p>
              <strong>Scenario:</strong> Login API over HTTP is a security risk,
              while HTTPS encrypts the token during transmission.
            </p>
          </section>

          {/* METHODS */}
          <section id="methods">
            <h2>HTTP Methods</h2>
            <ul>
              <li>
                <strong>GET:</strong> Fetch user profile
              </li>
              <li>
                <strong>POST:</strong> Create order
              </li>
              <li>
                <strong>PUT:</strong> Update full user profile
              </li>
              <li>
                <strong>PATCH:</strong> Update email or phone
              </li>
              <li>
                <strong>DELETE:</strong> Cancel order
              </li>
              <li>
                <strong>OPTIONS:</strong> Supported methods
              </li>
              <li>
                <strong>HEAD:</strong> Headers only
              </li>
            </ul>

            <p>
              <strong>PATCH vs PUT:</strong> PATCH updates partial data, PUT
              replaces the entire resource.
            </p>
          </section>

          {/* STATUS CODES */}
          <section id="status-codes">
            <h2> What are HTTP Status Codes ?</h2>
            <p>
              HTTP Protocol is used everywhere from the server page to each
              service communication, deploying service to monitoring service.
              HTTP codes give an extension of HTTP protocol to debug network
              problems using curl, telnet able to check server availability,
              service responses
            </p>
            <p>
              HTTP status code is used for search engine optimization of how
              your pages get indexed, as well as how search engines perceive the
              performance of your site.
            </p>
            <h3>Why Do We Need Many HTTP Statuses?</h3>
            <p>
              It reduces the complexity of client and server errors. It is also
              one reason why Microservice and HATEOAS are easy to definable.
            </p>
            <p>
              The initial line of the server’s response indicates the HTTP
              version and a three-digit status code. Server reserves some
              default error messages (404 not found or 500 internal server
              error). Each server(Tomcat, Redhat, Web server) reserves some HTTP
              status for inbuilt functionality. It is also possible to manually
              configure the status code.
            </p>
            <h3>HTTP Response Status Codes</h3>
            <ul>
              <li style={{ textalign: "justify" }}>
                <a
                  href="#informational-responses"
                  rel="noopener"
                >
                  Informational responses (100–199)
                </a>
              </li>
              <li style={{ textalign: "justify" }}>
                <a
                  href="#successful-responses"
                  rel="noopener"
                >
                  Successful responses (200–299)
                </a>
              </li>
              <li style={{ textalign: "justify" }}>
                Redirection messages (300–399)
              </li>
              <li style={{ textalign: "justify" }}>
                <a
                  href="#client-error-responses"
                  rel="noopener"
                >
                  Client error responses (400–499)
                </a>
              </li>
              <li style={{ textalign: "justify" }}>
                <a
                  href="#server-error-responses"
                  rel="noopener"
                >
                  Server error responses (500–599)
                </a>
              </li>
            </ul>

<h3 id="informational-responses">Informational Responses</h3>
<figure className="table"><table ><thead><tr><th>Message&nbsp;</th><th>Description</th></tr></thead><tbody><tr><td>100 Continue</td><td>readies for the client to continue with a request.</td></tr><tr><td><strong>101</strong> Switching Protocols</td><td>that it will be switching to another protocol.</td></tr><tr><td>102 Processing (WebDAV)</td><td>This code tells us that server is processing the request but at the current time, no response is available.</td></tr><tr><td>103 Early Hints</td><td>This is to be used with the Link Header, where the preloading resources are started and the server prepares responses.</td></tr></tbody></table></figure>


<h3 id="successful-responses">Success Response</h3>
<figure className="table"><table><thead><tr><th>Message&nbsp;</th><th>Description</th></tr></thead><tbody><tr><td>200 OK</td><td>For a successful transaction.</td></tr><tr><td>201 Created</td><td>For creating an entry in the database or updating details.</td></tr><tr><td>202 Accepted&nbsp;</td><td>It might accept the request but not decided on going to create an entry.</td></tr><tr><td>203 Non-Authoritative Information &nbsp;</td><td>The information given by the user is not the original owner of the request.</td></tr><tr><td>204 No Content&nbsp;</td><td>The response came without no entity-body.</td></tr><tr><td>205 Reset Content&nbsp;</td><td>The browser should clear the form used for this transaction for additional input.</td></tr><tr><td>206 Partial Content&nbsp;</td><td>The server is returning partial data of the size requested.</td></tr><tr><td>207 Multi-Status (WebDAV)</td><td>It has the work to convey multiple resources, in that case, where more than one code can be correct.&nbsp;</td></tr><tr><td>208 Already Reported (WebDAV)</td><td>It is used to avoid the repeating internal members of different binding to the same data. it is used inside <strong>&lt;dav:propstat&gt;.</strong></td></tr><tr><td>226 IM Used (HTTP Data Encoding)</td><td>Here, the response is represented by combining the result of one or more manipulations with the current instance.</td></tr></tbody></table></figure>
           
           <h3>Redirection Messages</h3>
           <figure className="table"><table><thead><tr><th>Message&nbsp;</th><th>Description</th></tr></thead><tbody><tr><td><p style={{ textalign: "justify" }}>300 Multiple Choices</p>

</td><td><p style={{ textalign: "justify" }}>The requested URL refers to more than one resource.</p>

</td></tr><tr><td><p style={{ textalign: "justify" }}>301 Moved Permanently</p>

</td><td><p style={{ textalign: "justify" }}>The requested URL is no longer used by the server. The new location for the requested document is specified in the&nbsp;</p>

<p style={{ textalign: "justify" }}>Location header. &nbsp;All future requests for the document should use the new URL.</p>

</td></tr><tr><td><p style={{ textalign: "justify" }}>302 Found&nbsp;</p>

</td><td><p style={{ textalign: "justify" }}>The redirected URL is found current working URL location</p>

</td></tr><tr><td><p style={{ textalign: "justify" }}>303 See Other &nbsp;</p>

</td><td><p style={{ textalign: "justify" }}>The requested URL can be found at a different URL (specified in the Location header) and should be retrieved by a&nbsp;</p>

<p style={{ textalign: "justify" }}>GET on that resource.</p>

</td></tr><tr><td><p style={{ textalign: "justify" }}>304 Not Modified</p>

</td><td><p style={{ textalign: "justify" }}>The URL has not been modified since the specified date. The entity body is not sent, and the client should use its&nbsp;</p>

<p style={{ textalign: "justify" }}>own local copy.</p>

</td></tr><tr><td><p style={{ textalign: "justify" }}>305 Use Proxy</p>

</td><td><p style={{ textalign: "justify" }}>The requested URL must be accessed through the proxy in the Location header.</p>

</td></tr><tr><td><p style={{ textalign: "justify" }}>307 Temporary Redirect&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;&nbsp;</p>

</td><td><p style={{ textalign: "justify" }}>The requested URL has moved, but only temporarily.</p>

</td></tr><tr><td><p style={{ textalign: "justify" }}>308 Permanent Redirect</p>

</td><td><p style={{ textalign: "justify" }}>It is used for locating another URL Permanently. It is done by the 'Location:'. HTTP Response Code <strong>301 Moved&nbsp;</strong></p>

<p style={{ textalign: "justify" }}><strong>Permanently </strong>is similar to <strong>308 Permanent Redirect.&nbsp;</strong></p>

</td></tr></tbody></table></figure>
           

           <h3 id="client-error-responses">Client Error Responses</h3>
           <figure className="table"><table><thead><tr><th>Message &nbsp;</th><th>Description</th></tr></thead><tbody><tr><td>400 Bad Request&nbsp;</td><td>This response code indicates that the server detected a syntax error in the client’s request.</td></tr><tr><td>401 Unauthorized&nbsp;</td><td>The service failed because of not give proper parameters like &nbsp;username, password</td></tr><tr><td>402 Payment Required&nbsp;</td><td>This code is not yet implemented in HTTP.</td></tr><tr><td>403 Forbidden&nbsp;</td><td>The request was denied for a reason the server does not want to (or has no means to) indicate to the client.</td></tr><tr><td>404 Not Found&nbsp;</td><td>The document at the specified URL does not exist.</td></tr><tr><td>405 Method Not Allowed&nbsp;</td><td>

<p>This code is given with the Allow header and indicates that the method used by the client is not supported</p>



<p>for this URL.</p>

</td></tr><tr><td>406 Not Acceptable</td><td>

<p>The URL specified by the client exists, but not in a format preferred by the client. Along with this code, the server&nbsp;</p>



<p>provides the Content-Language, Content-Encoding, and Content-type headers.</p>

</td></tr><tr><td>407 Proxy Authentication Required&nbsp;</td><td>The proxy server needs to authorize the request before forwarding it. Used with the Proxy-Authenticate header.</td></tr><tr><td>408 Request Time-out&nbsp;</td><td>

<p>This response code means the client did not produce a full request within some predetermined time (usually&nbsp;</p>



<p>specified in the server’s configuration), and the server is disconnecting the network connection.</p>

</td></tr><tr><td>409 Conflict&nbsp;</td><td>

<p>This code indicates that the request conflicts with another request or with the server’s configuration.&nbsp;</p>



<p>Information about the conflict should be returned in the data portion of the reply. For example, this response&nbsp;</p>



<p>code could be given when a client’s request would cause integrity problems in a database.</p>

</td></tr><tr><td>410 Gone</td><td>This code indicates that the requested URL no longer exists and has been permanently removed from the server.</td></tr><tr><td>411 Length Required</td><td>The server will not accept the request without a Content-length header supplied in the request.</td></tr><tr><td>412 Precondition Failed&nbsp;</td><td>The condition specified by one or more If... headers in the request evaluated to false.</td></tr><tr><td>413 Request Entity Too Large&nbsp;</td><td>The server will not process the request because its entity-body is too large.</td></tr><tr><td>414 Request URL Too Long&nbsp;</td><td>The server will not process the request because its request URL is too large.</td></tr><tr><td>415 Unsupported Media Type</td><td>The server will not process the request because its entity-body is in an unsupported format.</td></tr><tr><td>416 Request Range Not Satisfiable&nbsp;</td><td>The requested byte range is not available and is out of bounds.</td></tr><tr><td>417 Expectation Failed&nbsp;</td><td>The server is unable to meet the demands of the Expect header given by the client.</td></tr><tr><td>418 I'm a teapot</td><td>It is used to attempt to refuse coffee with a teapot.</td></tr><tr><td>421 Misdirected Request</td><td>

<p>This request is used to direct to a server that is not able to produce any response. Generally, it is sent&nbsp;</p>



<p>by a &nbsp;server for not producing the authorities present in the request URL.</p>

</td></tr><tr><td>422 Unprocessable Content (WebDAV)</td><td>It occurs when the request is not followed because of semantic errors.</td></tr><tr><td>423 Locked (WebDAV)</td><td>It refers to the locked resources.</td></tr><tr><td>424 Failed Dependency (WebDAV)</td><td>It occurs due to the failure of a recent request.</td></tr><tr><td>425 Too Early</td><td>It occurs when a request is made that is not processed yet.</td></tr><tr><td>426 Upgrade Required</td><td>

<p>It refers to when the current protocol is not considered and the system is waiting so that any other&nbsp;</p>



<p>protocol is generated. It generates the upgrade header.</p>

</td></tr><tr><td>428 Precondition Required</td><td>This request comes when the request is conditional. This is used to help with the lost update problem.</td></tr><tr><td>429 Too Many Requests</td><td>This error comes when multiple requests are made in a certain amount of time.</td></tr><tr><td>431 Request Header Fields Too Large</td><td>This comes when header fields are large enough to proceed. This request comes in that condition.</td></tr><tr><td>451 Unavailable for Legal Reasons</td><td><p style={{ textalign: "justify" }}>It occurs when any request is not fulfilled because of some legal reasons, it can be due to a web&nbsp;</p>



<p>page being censored.</p>

</td></tr></tbody></table></figure>


<h3 id="server-error-responses">Server Error Responses</h3>
<figure className="table"><table><thead><tr><th>Message</th><th>Description</th></tr></thead><tbody><tr><td>500 Internal Server Error</td><td>This code indicates that a part of the server encountered a configuration error.</td></tr><tr><td>501 Not Implemented&nbsp;</td><td>This code indicates that the client requested an action that cannot be performed by the server.</td></tr><tr><td>502 Bad Gateway&nbsp;</td><td>This code indicates that the server (or proxy) encountered invalid responses from another server (or proxy).</td></tr><tr><td>503 Service Unavailable&nbsp;</td><td>This code means that the service is temporarily unavailable, but should be restored in the future.</td></tr><tr><td>504 Gateway Time-out</td><td>This code means that the service is temporarily unavailable, but should be restored in the future.</td></tr><tr><td>505 HTTP Version Not Supported</td><td>Internet Engineering Task Force (IETF) Publish information usage of standard code.</td></tr><tr><td>506 Variant Also Negotiates</td><td>

<p>It comes with a configuration error. It is engaged in negotiation., but does not exist at</p>



<p>the endpoint of the negotiation process.</p>

</td></tr><tr><td>507 Insufficient Storage (WebDAV)</td><td>It comes when the request is not to be completed because of the proper storage.</td></tr><tr><td>508 Loop Detected</td><td>This error comes when there is an infinite loop.</td></tr><tr><td>510 Not Extended</td><td>Some more extensions are required for the server for fulfilling it.</td></tr><tr><td>511 Network Authentication Required</td><td>This indicates that it requires verifying network access.</td></tr></tbody></table></figure>

<h3>Most Ask in Interview</h3>

            <ul>
              <li>200 – Success</li>
              <li>201 – Resource Created</li>
              <li>204 – No Content</li>
              <li>400 – Bad Request</li>
              <li>401 – Unauthorized</li>
              <li>403 – Forbidden</li>
              <li>404 – Not Found</li>
              <li>409 – Conflict</li>
              <li>500 – Internal Server Error</li>
            </ul>
          </section>

          {/* HEADERS & PAYLOAD */}
          <section id="headers">
            <h2>Headers & Payload</h2>

            <h3>Headers</h3>
            <ul>
              <li>Authorization</li>
              <li>Content-Type</li>
              <li>Accept</li>
              <li>User-Agent</li>
            </ul>

            <h3>Payload Example</h3>
            <ul>
              <li>Valid payload → 200 OK</li>
              <li>Invalid payload → 400 Bad Request</li>
              <li>Missing Content-Type → 415 Unsupported Media Type</li>
            </ul>
          </section>

          {/* AUTH */}
          <section id="auth">
            <h2>Authentication, Token & JWT</h2>
            <ul>
              <li>Login API generates token</li>
              <li>Token stored in environment</li>
              <li>Token passed in Authorization header</li>
            </ul>

            <p>
              <strong>Token Errors:</strong> Expired token → 401, Invalid token
              → 403
            </p>
          </section>

          {/* POSTMAN */}
          <section id="postman">
            <h2>Postman: Collection & Environment</h2>
            <ul>
              <li>Auth APIs</li>
              <li>User APIs</li>
              <li>Order APIs</li>
              <li>Payment APIs</li>
            </ul>

            <p>Environments help manage Dev, QA, and Prod base URLs.</p>
          </section>

          {/* SCRIPTS */}
          <section id="scripts">
            <h2>Pre-request & Test Scripts</h2>
            <ul>
              <li>Generate dynamic data</li>
              <li>Create timestamp or UUID</li>
              <li>Extract token</li>
              <li>Validate response time and schema</li>
            </ul>
          </section>

          {/* REAL-TIME SCENARIOS */}
          <section id="scenarios">
            <h2>Real-Time API Testing Scenarios</h2>
            <ul>
              <li>Login API – valid → 200, invalid → 401</li>
              <li>Order creation – success → 201, out of stock → 409</li>
              <li>Token expired → 401</li>
              <li>Rate limit exceeded → 429</li>
              <li>Service unavailable → 503</li>
            </ul>
          </section>

          {/* SECURITY */}
          <section id="security">
            <h2>Security & Performance Testing</h2>
            <ul>
              <li>Token manipulation</li>
              <li>Role-based access control</li>
              <li>SQL injection prevention</li>
              <li>Response time & throughput validation</li>
            </ul>

            <p>
              <strong>Tools:</strong> JMeter, k6, Gatling
            </p>
          </section>

          <div className="toc-container">
            <strong>Interview Answer Template</strong>
            <p>
              “In my project, I validate APIs using Postman and Newman, verify
              request, response, headers, payload, authentication, and business
              logic, and automate collections in CI/CD.”
            </p>
            {/* <p>Read this to prepare inteview .... <NavLink to="/API-testing-cheatSheet">Must Read</NavLink></p> */}
          </div>
        </div>

        <footer className="fusion-meta-info">
          <div className="fusion-meta-info-wrapper">
            By{" "}
            <span className="vcard">
              <NavLink to="#author"> Manish Kumar </NavLink>
            </span>
            <span className="black-white-box-fusion-inline-sep"> |</span>
            <span> June 17th, 2025 </span>
            <span className="fusion-inline-sep">|</span>
            <NavLink className="black-white-box" to="/software-testing">
              Software Testing
            </NavLink>
          </div>
        </footer>

        <div className="sharing-box">
          <h4>Share This Story, Choose Your Platform!</h4>
          <div className="fusion-social-networks">
            <NavLink to="#" className="facebook">
              <i className="fab fa-facebook-f"></i>
            </NavLink>
         
            <NavLink to="https://www.linkedin.com/in/qa-coder-708418404/" target="_blank" rel="noreferrer" className="linkedin">
              <i className="fab fa-linkedin-in"></i>
            </NavLink>
            <NavLink to="https://wa.me/918299775823" target="_blank" rel="noreferrer" className="whatsapp">
              <i className="fab fa-whatsapp"></i>
            </NavLink>
            <NavLink to="#" className="email">
              <i className="fas fa-envelope"></i>
            </NavLink>
            <NavLink to="https://www.instagram.com/qacoder0/?hl=en" target="_blank" rel="noreferrer" className="instagram">
              <i className="fab fa-instagram"></i>
            </NavLink>
          </div>
        </div>
      </main>
    </div>
  );
}
